import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Endpoints } from "../lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [professions, setProfessions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [workstations, setWorkstations] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [skeletons, setSkeletons] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [solving, setSolving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);

  const loadAll = useCallback(async () => {
    const [cfg, prof, ag, ws, req, sk, abs, sch] = await Promise.all([
      Endpoints.config(),
      Endpoints.professions(),
      Endpoints.agents(),
      Endpoints.workstations(),
      Endpoints.requirements(),
      Endpoints.skeletons(),
      Endpoints.absences(),
      Endpoints.schedule(),
    ]);
    setConfig(cfg);
    setProfessions(prof);
    setAgents(ag);
    setWorkstations(ws);
    setRequirements(req);
    setSkeletons(sk);
    setAbsences(abs);
    setSchedule(sch);
  }, []);

  useEffect(() => {
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  const refreshLists = useCallback(async () => {
    const [prof, ag, ws, req, sk, abs] = await Promise.all([
      Endpoints.professions(),
      Endpoints.agents(),
      Endpoints.workstations(),
      Endpoints.requirements(),
      Endpoints.skeletons(),
      Endpoints.absences(),
    ]);
    setProfessions(prof);
    setAgents(ag);
    setWorkstations(ws);
    setRequirements(req);
    setSkeletons(sk);
    setAbsences(abs);
  }, []);

  const solve = useCallback(async () => {
    setSolving(true);
    try {
      const res = await Endpoints.solve();
      setSchedule(res);
      toast.success("Schedule generated", {
        description: `${res.status} · penalty ${res.objective_value?.toLocaleString()} · ${res.solve_time_ms} ms`,
      });
      return res;
    } catch (e) {
      toast.error("Solve failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSolving(false);
    }
  }, []);

  const recalculate = useCallback(async (freezeDay, freezeTime) => {
    setSolving(true);
    try {
      const res = await Endpoints.recalculate(freezeDay, freezeTime);
      setSchedule(res);
      toast.success("Re-optimised future slots", {
        description: `Frozen up to ${freezeDay} ${freezeTime} · ${(res.solve_time_ms / 1000).toFixed(1)} s`,
      });
      return res;
    } catch (e) {
      toast.error("Recalculation failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSolving(false);
    }
  }, []);

  const reseed = useCallback(async () => {
    setSolving(true);
    try {
      await Endpoints.seed();
      await loadAll();
      const res = await Endpoints.solve();
      setSchedule(res);
      toast.success("Demo dataset restored & solved");
    } finally {
      setSolving(false);
    }
  }, [loadAll]);

  const value = {
    config, professions, agents, workstations, requirements, skeletons, absences,
    schedule, loading, solving, selectedDay, setSelectedDay,
    solve, recalculate, reseed, refreshLists, setSchedule,
  };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
